import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { Header } from "@/layouts/Header";
import { Footer } from "@/layouts/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealObserver } from "@/hooks/useRevealObserver";
import {
  MapPin,
  ArrowRight,
  Target,
  Sparkles,
  ShieldCheck,
  Workflow,
  LineChart,
  Cpu,
  Users,
  CheckCircle,
  Headset,
  Brain,
  Search,
  Lock,
  Globe,
  Server,
  Briefcase
} from "lucide-react";
import { SEOHead } from "@/features/shared/SEOHead";
import { generateProfessionalServiceSchema } from "@/lib/business-schema";

import parthPhoto from "@/assets/parth-bhanushali.webp";
import denisPhoto from "@/assets/denis-mahoney.webp";

const About = () => {
  useRevealObserver();
  const navigate = useNavigate();



  const methodSteps = [
    {
      title: "Get Found",
      description: "Local SEO, content, and trust signals that make your business the obvious choice.",
      bullets: ["Authority Content", "Local Dominance", "Search Visibility"],
      icon: Search
    },
    {
      title: "Capture & Nurture",
      description: "Automated funnels, AI responders, and CRM workflows that convert attention into revenue.",
      bullets: ["Smart Pipelines", "AI Follow-up", "Automated Booking"],
      icon: Workflow
    },
    {
      title: "Scale Profitably",
      description: "Once your foundation is strong, we layer ads, outbound, and intelligence for sustainable growth.",
      bullets: ["Data-Driven Ads", "Outbound Systems", "Revenue Intelligence"],
      icon: LineChart
    }
  ];

  const values = [
    {
      title: "Results First",
      description: "Every system must prove ROI.",
      icon: Target
    },
    {
      title: "Ontario Proud",
      description: "Built here. Designed for here.",
      icon: MapPin
    },
    {
      title: "Technology Forward",
      description: "AI, automation, and integration without the enterprise complexity.",
      icon: Cpu
    },
    {
      title: "Partnership Driven",
      description: "Your success becomes our operating metric.",
      icon: Users
    }
  ];

  const impactStats = [
    { value: "7 days", label: "From kickoff to live" },
    { value: "7–12", label: "Tools replaced by one system" },
    { value: "100%", label: "Leads followed up, automatically" },
    { value: "24/7", label: "AI response & monitoring" }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <SEOHead
        title="About Frayze | Thunder Bay Web Design & CRM Experts"
        description="Meet the team behind Northern Ontario's leading growth platform. Built by Denis Mahoney and Parth Bhanushali for lead-driven businesses."
        keywords="Frayze Technologies, Thunder Bay tech company, Northern Ontario web design, business CRM Canada, growth automation Ontario"
        ogTitle="About Frayze | Thunder Bay Web Design & CRM Experts"
        ogDescription="Meet the team behind Northern Ontario's leading business growth platform. Built for lead-driven businesses across Northern Ontario."
        twitterTitle="About Frayze | Thunder Bay Web Design & CRM Experts"
        twitterDescription="Meet the team behind Northern Ontario's leading business growth platform. Built for lead-driven businesses across Northern Ontario."
        canonical="https://frayze.ca/about/"
        robots="index, follow, max-snippet:-1, max-image-preview:large"
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "AboutPage",
              "@id": "https://frayze.ca/about",
              "name": "About Frayze Technologies Inc.",
              "description": "Meet the team behind Northern Ontario's leading growth platform. Built by Denis Mahoney and Parth Bhanushali for lead-driven businesses.",
              "url": "https://frayze.ca/about"
            },
            generateProfessionalServiceSchema()
          ]
        }}
      />

      <main className="pt-32 pb-16">
        {/* Hero Section */}
        <section className="relative overflow-hidden pt-10 pb-20 lg:pt-20 lg:pb-32">
          <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#EEF3FF] via-[#F8F5FF] to-[#EAF6FF]">
            <div className="absolute inset-0 bg-grid-white/[0.05]" />
          </div>
          <div className="container mx-auto px-6 text-center">
            <Badge variant="outline" className="mb-6 bg-white/50 backdrop-blur border-primary/20 text-primary px-4 py-1 text-sm font-medium">
              <MapPin className="h-3 w-3 mr-2" />
              Built in Thunder Bay. Engineered for all of Canada.
            </Badge>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-8">
              The Catch-Every-Lead System for <br className="hidden md:block" />
              <span className="text-primary bg-clip-text text-transparent bg-gradient-to-r from-primary to-violet-600">
                Lead-Driven Businesses
              </span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-10 leading-relaxed">
              Built in Thunder Bay for businesses where a missed lead is a four-figure loss: auto dealers, trades, and local service. Frayze connects your website, CRM, and AI follow-up into one system, so more leads turn into paying customers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="text-lg px-8 py-6 shadow-lg shadow-primary/20" onClick={() => navigate("/book-consultation/")}>
                Book My Free Strategy Call
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button variant="outline" size="lg" className="text-lg px-8 py-6 bg-white/50" onClick={() => navigate("/case-studies/")}>
                See Our Work
              </Button>
            </div>
          </div>
        </section>

        {/* Origin Story Section */}
        <section className="py-20 lg:py-24 bg-white relative">
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <div>
                  <h2 className="text-3xl lg:text-4xl font-bold mb-4">Why Frayze Exists</h2>
                  <p className="text-xl font-medium text-primary">The tools existed. The system didn't.</p>
                </div>
                <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    Northern Ontario businesses were losing money to disconnected tools: <Link to="/solutions/crm-automation/" className="underline decoration-primary/40 hover:text-primary">CRMs that didn't talk to websites</Link>, <Link to="/solutions/lead-generation-system/" className="underline decoration-primary/40 hover:text-primary">marketing that didn't sync with sales</Link>, and <Link to="/solutions/ad-management-scale/" className="underline decoration-primary/40 hover:text-primary">ads that ran while leads slipped through the cracks</Link>.
                  </p>
                  <p>
                    Founders Denis Mahoney and Parth Bhanushali saw the same pattern in every trade:
                    <strong className="text-foreground block mt-2">The growth problem was never traffic. It was infrastructure.</strong>
                  </p>
                  <p>
                    Denis saw it firsthand in Thunder Bay&apos;s automotive and trades shops, where clunky CRMs and manual work meant lost revenue. Parth, an AI and automation engineer, had fixed the same problems for big companies. So they built it for local operators.
                  </p>
                </div>
              </div>
              <div className="bg-slate-50 border rounded-3xl p-10 lg:p-12 shadow-sm">
                <h3 className="text-2xl font-bold mb-6">Together, they built Frayze:</h3>
                <p className="text-xl text-slate-700 leading-relaxed mb-8">
                  One connected system that replaces 7–12 tools, ends the chaos, and turns your customer journey into a predictable engine for growth.
                </p>
                <div className="flex items-center gap-4 text-sm font-semibold text-slate-500 border-t pt-6">
                  <MapPin className="h-5 w-5 text-primary" />
                  <span>Serving Thunder Bay, Toronto, Sudbury, and beyond.</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Canada Section */}
        <section className="py-20 bg-slate-900 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.18),transparent_60%)]"></div>
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <Badge variant="outline" className="text-white border-white/30 mb-4">Why Canada?</Badge>
              <h2 className="text-3xl md:text-5xl font-bold mb-6">Frayze is proudly Canadian-owned and operated.</h2>
              <p className="text-xl text-slate-300">
                We serve businesses that want reliability, compliance, and real partnership, not offshore outsourcing or fragmented tools.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {[
                { icon: Globe, title: "Local Support", desc: "Real support from Canadian experts who understand your market." },
                { icon: ShieldCheck, title: "Canadian Data Compliance", desc: "PIPEDA aligned infrastructure to keep your customer data safe." },
                { icon: Headset, title: "Bilingual-Ready Systems", desc: "Platforms built to support both official languages." },
                { icon: Server, title: "SOC 2 Infrastructure", desc: "Enterprise-grade security and reliability." },
                { icon: CheckCircle, title: "North American Uptime", desc: "Reliable performance you can trust." },
                { icon: Users, title: "Built for SMBs", desc: "Designed for Canadian businesses, not Silicon Valley startups." },
              ].map((item, i) => (
                <div key={i} className={`reveal ${i % 2 === 1 ? "reveal-delay-1" : ""} bg-white/5 border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-colors`}>
                  <item.icon className="h-8 w-8 text-blue-400 mb-4" />
                  <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-400">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 lg:py-24 bg-slate-50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">Our Mission</h2>
              <p className="text-xl text-muted-foreground leading-relaxed">
                To give Northern Ontario businesses access to enterprise-grade websites, CRM, and automation, in a simple, localized platform designed for real operators.
              </p>
              <div className="grid sm:grid-cols-2 gap-4 text-left pt-8 max-w-2xl mx-auto">
                {["Canadian innovation deserves a global stage", "Local SMBs should have world-class tools", "Technology should remove complexity", "Growth systems should be ROI-driven"].map((belief, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground/80 font-medium">{belief}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* The Frayze Method */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <Badge variant="secondary" className="mb-4">The Frayze Method</Badge>
              <h2 className="text-4xl font-bold mb-6">A Proven 3-Stage Growth Blueprint</h2>
              <p className="text-lg text-muted-foreground">One system usually costs less than the stack of separate tools it replaces, and nothing falls between them.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {methodSteps.map((step, index) => (
                <Card key={index} className={`reveal ${index % 2 === 1 ? "reveal-delay-1" : ""} lift-hover border-0 shadow-lg bg-slate-50/50`}>
                  <CardContent className="p-8">
                    <div className="h-12 w-12 bg-primary/10 rounded-xl flex items-center justify-center text-primary mb-6">
                      <step.icon className="h-6 w-6" />
                    </div>
                    <div className="text-sm font-bold text-primary mb-2 uppercase tracking-wider">Stage {index + 1}</div>
                    <h3 className="text-2xl font-bold mb-4">{step.title}</h3>
                    <p className="text-muted-foreground mb-6">{step.description}</p>
                    <ul className="space-y-3">
                      {step.bullets.map((bullet, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm font-medium">
                          <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                          {bullet}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Founders */}
        <section className="py-24 bg-slate-900 text-white">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">Meet the Founders</h2>
              <p className="text-xl text-slate-400">Two builders. One vision. Unified growth.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto">
              {/* Denis */}
              <div className="reveal lift-hover bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/[0.07] transition-all">
                <div className="flex flex-col items-center text-center">
                  <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white/10 mb-6">
                    <img src={denisPhoto} alt="Man in a suit speaking into a microphone, representing Frayze Technologies Inc. at a local business event in Thunder Bay." className="w-full h-full object-cover" />
                  </div>
                  <h3 className="text-2xl font-bold mb-1">Denis Mahoney</h3>
                  <p className="text-blue-400 font-medium text-sm mb-6">Co-Founder &amp; Chief Automation Officer</p>
                  <p className="text-slate-300 mb-6 text-sm leading-relaxed">
                    Architects the systems, strategy, and revenue frameworks behind Frayze. Expert in automation, CRM engineering, growth workflows, and operational efficiency.
                  </p>
                  <Badge variant="outline" className="text-xs border-white/20 text-slate-400">Thunder Bay, ON</Badge>
                </div>
              </div>
              {/* Parth */}
              <div className="reveal reveal-delay-1 lift-hover bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/[0.07] transition-all">
                <div className="flex flex-col items-center text-center">
                  <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white/10 mb-6">
                    <img src={parthPhoto} alt="Professional headshot of a smiling man in a black suit and tie, representing Frayze Technologies Inc., a digital agency focused on web design and SEO services in Thunder Bay." className="w-full h-full object-cover" />
                  </div>
                  <h3 className="text-2xl font-bold mb-1">Parth Bhanushali</h3>
                  <p className="text-blue-400 font-medium text-sm mb-6">Co-Founder | Technical Architect</p>
                  <p className="text-slate-300 mb-6 text-sm leading-relaxed">
                    Leads platform development, AI integration, and scalable automation logic. Specialist in LLMs, voice agents, systems architecture, and cloud frameworks.
                  </p>
                  <Badge variant="outline" className="text-xs border-white/20 text-slate-400">Thunder Bay, ON</Badge>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">Our Values</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {values.map((value, i) => (
                <div key={i} className={`reveal ${i % 2 === 1 ? "reveal-delay-1" : ""} lift-hover text-center p-6 border rounded-xl hover:shadow-md transition-shadow`}>
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 text-primary rounded-full mb-4">
                    <value.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{value.title}</h3>
                  <p className="text-sm text-muted-foreground">{value.description}</p>
                </div>
              ))}
            </div>

            {/* Join the Ecosystem */}
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="group relative overflow-hidden rounded-2xl bg-slate-900 p-8 text-white">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative z-10">
                  <div className="h-12 w-12 bg-white/10 rounded-xl flex items-center justify-center mb-6">
                    <Briefcase className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">Join the Team</h3>
                  <p className="text-slate-300 mb-6">
                    We're building the future of Canadian growth tech. View open roles and help us ship software that matters.
                  </p>
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 hover:text-white" onClick={() => navigate("/careers/")}>
                    View Careers
                  </Button>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* Security & Impact */}
        <section className="py-20 bg-slate-50 border-y">
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-16">
              {/* Security */}
              <div>
                <Badge variant="outline" className="mb-4 text-primary border-primary">Security & Compliance</Badge>
                <h2 className="text-3xl font-bold mb-6">Built on SOC 2 Type II infrastructure.</h2>
                <p className="text-muted-foreground mb-8">
                  Enterprise security for businesses of any size. You get end-to-end encryption, redundant backups, and GDPR & PIPEDA alignment.
                </p>
                <Button variant="outline" onClick={() => navigate("/security/")}>
                  View Security Details
                </Button>
              </div>
              {/* Impact */}
              <div>
                <Badge variant="outline" className="mb-4 text-primary border-primary">How We Work</Badge>
                <h2 className="text-3xl font-bold mb-6">What You Can Count On.</h2>
                <div className="grid grid-cols-2 gap-6">
                  {impactStats.map((stat, i) => (
                    <div key={i}>
                      <div className="text-3xl font-bold text-primary mb-1">{stat.value}</div>
                      <div className="text-sm text-muted-foreground">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-primary text-primary-foreground text-center">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">Join the Businesses Growing with Frayze</h2>
            <p className="text-xl text-primary-foreground/80 max-w-2xl mx-auto mb-10">
              From Thunder Bay to Toronto, Canadian businesses are scaling with one connected system instead of a messy stack of tools. See what it can do for yours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" className="text-lg px-8 py-6 font-bold" onClick={() => navigate("/book-consultation/")}>
                Book My Free Strategy Call
              </Button>
              <Button size="lg" variant="outline" className="text-lg px-8 py-6 bg-transparent border-white text-white hover:bg-white/10" onClick={() => navigate("/digital-audit/")}>
                Get My Free Audit
              </Button>
            </div>
          </div>
        </section>

      </main>

      <Footer />

    </div>
  );
};

export default About;
